import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  ArrowLeft, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  FileText, 
  CreditCard, 
  Calendar,
  DollarSign,
  Edit
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { OrdenespagoService } from '../../services/ordenespagoService';
import { getValorUfVigente } from '../../services/ufDiariaService';
import { formatUF, formatCLP } from '../../styles/tokens/formatters';
import { ordenPagoEstados, getOrdenPagoColor, getOrdenPagoColorValue } from '../../styles/tokens/colors';
import { useSupabaseClient } from '../../lib/supabase';
import { formatearRut } from '../../lib/rut';
import { TarjetaTrazabilidad } from '../../components/ordenes-pago/Trazabilidad';
import { trazabilidadDeOrden } from '../../lib/ordenesPago/trazabilidad';
import { fmtFechaHoraDb } from '../../lib/fechas';

const OrdenespagoDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [orden, setOrden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  // UF vigente para convertir a pesos. null = sin valor en uf_diaria; se muestra
  // solo UF en vez de asumir una tasa.
  const [uf, setUf] = useState(null);
  const { toast } = useToast();
  // Pagos y certificados tienen RLS que exige rol `authenticated`: con el cliente
  // anónimo la cadena vuelve vacía aunque las filas existan.
  const supabaseClient = useSupabaseClient();

  useEffect(() => {
    if (id && supabaseClient) {
      fetchOrdenDetails();
    }
    getValorUfVigente().then(setUf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, supabaseClient]);

  const fetchOrdenDetails = async () => {
    try {
      setLoading(true);
      const ordenData = await OrdenespagoService.getOrdenPagoById(id, supabaseClient);
      setOrden(ordenData);
    } catch (error) {
      console.error('Error fetching orden details:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar la orden de pago",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarEstado = async (nuevoEstado) => {
    try {
      setUpdatingStatus(true);
      await OrdenespagoService.cambiarEstado(id, nuevoEstado);
      toast({
        title: "Éxito",
        description: "Estado actualizado correctamente"
      });
      fetchOrdenDetails();
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast({
        title: "Error",
        description: "No se pudo cambiar el estado",
        variant: "destructive"
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const variants = {
      pendiente: { variant: 'secondary', icon: Clock },
      procesando: { variant: 'default', icon: RefreshCw },
      pagado: { variant: 'default', icon: CheckCircle },
      expirado: { variant: 'destructive', icon: AlertTriangle },
      cancelado: { variant: 'outline', icon: XCircle }
    };

    const config = variants[estado] || variants.pendiente;
    const Icon = config.icon;
    const bgColor = getOrdenPagoColorValue(estado, 'bg');
    const borderColor = getOrdenPagoColorValue(estado, 'border');
    const textColor = getOrdenPagoColorValue(estado, 'dark');

    return (
      <Badge 
        variant={config.variant} 
        className="flex items-center gap-2 text-sm px-3 py-1"
        style={{
          backgroundColor: bgColor,
          borderColor: borderColor,
          color: textColor
        }}
      >
        <Icon className="h-4 w-4" style={{ color: textColor }} />
        {estado.charAt(0).toUpperCase() + estado.slice(1)}
      </Badge>
    );
  };

  // Tanto ordenes_pago.monto como pagos.amount están en UF (moneda canónica).
  // El equivalente en pesos es referencial al valor UF de hoy — no es el monto
  // que se cobró; ese lo congela el cargo procesado (valor_uf_cobro).
  const montoEnPesos = (montoUf) => {
    const valorUf = uf?.valorClp;
    if (!valorUf) return null;
    const numerico = parseFloat(montoUf);
    if (isNaN(numerico)) return null;
    return formatCLP(numerico * valorUf);
  };

  // Hora de Chile, y con el instante normalizado: esta pantalla mezcla columnas
  // con zona (`ordenes_pago.created_at`) con otras sin ella
  // (`pagos.transaction_date`), y `new Date()` sobre las segundas las corría 3 o
  // 4 horas — por eso el pago aparecía después de su propio certificado.
  const formatDate = (dateString) => fmtFechaHoraDb(dateString);

  const isExpired = (fechaExpiracion) => {
    if (!fechaExpiracion) return false;
    return new Date(fechaExpiracion) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Cargando detalles de la orden...</p>
        </div>
      </div>
    );
  }

  if (!orden) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Orden no encontrada</h2>
        <p className="text-muted-foreground mb-4">La orden de pago solicitada no existe o no tienes permisos para verla.</p>
        <Button onClick={() => navigate('/ordenes-pago')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a la lista
        </Button>
      </div>
    );
  }

  // Teléfono y dirección del titular viven en `contrataciones.datos_titular`,
  // no como columnas propias de la tabla.
  const titular = orden.contrataciones?.datos_titular || {};
  const direccionTitular = [titular.direccion, titular.numero, titular.comuna]
    .filter(Boolean)
    .join(' ');
  const { pago: pagoPrincipalOrden } = trazabilidadDeOrden(orden);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/ordenes-pago')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              Orden de Pago <span className="font-mono">{orden.codigo || ''}</span>
            </h1>
            <p className="text-muted-foreground font-mono text-xs">{orden.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/ordenes-pago/${id}/editar`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Estado y Monto */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Estado de la Orden</span>
                {getEstadoBadge(orden.estado)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Monto</label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-[var(--yool-success)]" />
                    <div>
                      <span className="text-2xl font-bold text-[var(--yool-success)]">
                        {formatUF(orden.monto)}
                      </span>
                      {montoEnPesos(orden.monto) && (
                        <p className="text-sm text-muted-foreground">
                          {montoEnPesos(orden.monto)} al valor UF de hoy
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Cambiar Estado</label>
                  <Select 
                    value={orden.estado} 
                    onValueChange={handleCambiarEstado}
                    disabled={updatingStatus}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="procesando">Procesando</SelectItem>
                      <SelectItem value="pagado">Pagado</SelectItem>
                      <SelectItem value="expirado">Expirado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {isExpired(orden.fecha_expiracion) && orden.estado === 'pendiente' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Orden Expirada</span>
                  </div>
                  <p className="text-sm text-[var(--yool-error)] mt-1">
                    Esta orden ha expirado y debe ser renovada o cancelada.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Información del Cliente */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Información del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {orden.contrataciones ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Nombre</label>
                      <p className="font-medium">{orden.contrataciones.nombre}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">RUT</label>
                      {/* Guardado canónico; los puntos son solo presentación. */}
                      <p className="font-medium">
                        {orden.contrataciones.rut ? formatearRut(orden.contrataciones.rut) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Estado del Contrato</label>
                      <Badge variant="outline">{orden.contrataciones.estado || 'N/A'}</Badge>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{orden.contrataciones.mail || 'N/A'}</span>
                    </div>
                    {/* `contrataciones` no tiene columnas `telefono` ni
                        `direccion`: viven dentro de `datos_titular`. */}
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{titular.telefono || 'N/A'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                      <span className="text-sm">{direccionTitular || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No hay información del cliente disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Trazabilidad: de qué contratación nació el pago, qué suscripción
              quedó viva y qué certificados se emitieron. */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Trazabilidad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TarjetaTrazabilidad orden={orden} formatearFecha={formatDate} />
            </CardContent>
          </Card>
        </div>

        {/* Panel Lateral */}
        <div className="space-y-6">
          {/* Fechas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Fechas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Fecha de Creación</label>
                <p className="font-medium">{formatDate(orden.created_at)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Última Actualización</label>
                <p className="font-medium">{formatDate(orden.updated_at)}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-muted-foreground">Fecha de Expiración</label>
                <p className={`font-medium ${
                  isExpired(orden.fecha_expiracion) && orden.estado === 'pendiente' 
                    ? 'text-[var(--yool-error)]' 
                    : ''
                }`}>
                  {formatDate(orden.fecha_expiracion)}
                </p>
                {isExpired(orden.fecha_expiracion) && orden.estado === 'pendiente' && (
                  <p className="text-xs text-[var(--yool-error)] mt-1">Expirada</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Pagos Relacionados */}
          {orden.pagos && orden.pagos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Pagos Relacionados
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {orden.pagos.map((pago) => (
                  <div
                    key={pago.id}
                    className={`border rounded-lg p-3 ${
                      pago.id === pagoPrincipalOrden?.id ? 'border-[var(--yool-success)]' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{formatUF(pago.amount)}</span>
                      <Badge variant={pago.status === 'completed' ? 'default' : 'secondary'}>
                        {pago.status}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p className="font-mono">{pago.codigo || pago.id}</p>
                      {pago.payment_provider && (
                        <p>
                          Medio: {pago.payment_provider}
                          {pago.card_last_four ? ` ····${pago.card_last_four}` : ''}
                        </p>
                      )}
                      <p>Sesión: {pago.session_id}</p>
                      <p>Fecha: {formatDate(pago.transaction_date)}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdenespagoDetails;